import messages from './netbank-login-lang.json';
import netbankLoginAccountNumber from './netbank-login-account-number/netbank-login-account-number.vue';
import netbankLoginIdentifier from './netbank-login-identifier/netbank-login-identifier.vue';
import netbankLoginQr from './netbank-login-qr/netbank-login-qr.vue';
import netbankLoginDisabled from './netbank-login-disabled/netbank-login-disabled.vue';
import eventBus from '../../common/js/event-bus/event-bus';
import getPosition from '../../common/js/get-position/get-position';
import queryString from 'query-string';
import VueFocus from 'vue-focus';
import modal from '../../common/components/modal/modal.vue';
import direktApi from '../../api/direkt-api';
import { useDirektIbCookieHandler } from '../../common/js/composables/direkt-ib-cookie-handler';
import { inject } from 'vue';
import VueCheckbox from '@ui-common/vue-checkbox';
import VueTooltip from '@ui-common/vue-tooltip';
import VueTab from '@ui-common/vue-tab';
import { isMobile } from '@ui-common/sf-js-utils';

const COOKIE_VISITED = 'portal-netbank-login-visited';
const COOKIE_PANEL_TYPE = 'portal-netbank-panel-type';
const COOKIE_USER_USES_NEW_IB = 'portal-netbank-uses-new-ib';
const GTM_LOGIN_ERROR_EVENT = 'direktBankLoginFailed';
const GTM_ACCOUNT_NUMBER_CODE = 'accountNumber';
const GTM_PERSONAL_ID_CODE = 'personalIdLogin';
const WEBSOCKET_CONNECTION_TIMEOUT = 1000;
const RESET_NEW_IB_COOKIE = 'enableDirektLogin';
export default {
	mixins: [VueFocus.mixin, isMobile],
	data() {
		return {
			fallbackActive: false,
			panelType: 'account-number',
			errorPopupVisible: false,
			reverseDetails: false,
			loginEnabled: true,
			stickyTop: null,
			structureId: null,
			gtmLoginErrorDetails: null,
			focusedTab: null,
			userUsesNewIb: false,
			hasStatus: false
		};
	},
	i18n: { messages },
	components: {
		netbankLoginAccountNumber,
		netbankLoginIdentifier,
		netbankLoginQr,
		netbankLoginDisabled,
		modal,
		VueCheckbox,
		VueTooltip,
		VueTab
	},
	props: {
		otpdirektUrl: {
			required: true
		},
		linkError: {
			required: true
		},
		qrCodeUrl: {
			required: true
		},
		atmBranchSearchUrl: {
			required: true
		},
		gmapsApiKey: {
			type: String,
			required: true
		}
	},
	setup() {
		const { deleteDirektCookie } = useDirektIbCookieHandler();
		const userHasDszk = inject('userHasDszk');
		return {
			userHasDszk,
			deleteDirektCookie
		};
	},
	watch: {
		hasErrorSlot: {
			handler(val) {
				if (val) {
					this.deleteDirektCookie();
				}
			},
			immediate: true
		},
		errorPopupVisible(val) {
			if (val) {
				this.openPopup();
			} else {
				this.$nextTick(() => {
					if (this.panelType == 'account-number') {
						this.$refs.netbankLoginAccountNumber.$refs.errorDetailsLink.focus();
					}
					if (this.panelType == 'identifier') {
						this.$refs.netbankLoginIdentifier.$refs.errorDetailsLink.focus();
					}
				});
				this.closePopup();
			}
		},
		userUsesNewIb(newVal) {
			this.$cookie.set(COOKIE_USER_USES_NEW_IB, newVal, { expires: '1Y', secure: true, SameSite: 'strict' });
		}
	},
	computed: {
		contentPlaceholderActive() {
			if (this.isiPhone()) {
				return false;
			}
			return !this.hasStatus;
		},
		hasErrorSlot() {
			return !!this.$slots['error-message'];
		}
	},
	methods: {
		isiPhone() {
			return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
		},
		changePanel(type) {
			this.panelType = type;
			this.$cookie.set(COOKIE_PANEL_TYPE, type, { expires: '10Y', secure: true, SameSite: 'strict' });
		},
		goToUrl(url) {
			window.location.href = url;
		},
		openPopup() {
			eventBus.$emit('toggleAriaHidden', true);
			this.$nextTick(() => {
				this.$refs.dialog.addEventListener('scroll', this.onScroll);
			});
			document.body.classList.add('modal-open');
		},
		closePopup() {
			eventBus.$emit('toggleAriaHidden', false);
			this.$refs.dialog.removeEventListener('scroll', this.onScroll);
			document.body.classList.remove('modal-open');
		},
		onScroll() {
			if (this.$refs.dialog) {
				let scrollTop = getPosition(this.$refs.dialog).y;
				this.stickyTop = scrollTop < 0;
			}
		},
		keyHandler(e) {
			if (this.errorPopupVisible) {
				e.preventDefault();
				if (e.which == 27) {
					this.errorPopupVisible = false;
				}
			}
		},
		handleDirektLoginEnabled(message) {
			this.loginEnabled = message.loginEnabled;
			this.hasStatus = true;
		},
		async showFallback() {
			try {
				const { data } = await direktApi.getFallback();
				this.handleDirektLoginEnabled(data);
			} catch (err) {
				console.error('Netbank Login json not available!');
			}
		},
		handleStompError() {
			if (!this.fallbackActive) {
				this.fallbackActive = true;
				this.showFallback();
			}
		},
		connectToWS() {
			const vm = this;
			const stompConfig = {
				onConnect() {
					vm.fallbackActive = false;
					vm.client.subscribe('/topic/direktlogin', ({ body }) => {
						vm.handleDirektLoginEnabled(JSON.parse(body));
					});
				},
				onStompError: vm.handleStompError,
				onWebSocketError: vm.handleStompError
			};
			this.client = direktApi.createWSClient(stompConfig);
			setTimeout(() => {
				if (!this.client.connected) {
					this.showFallback();
				}
			}, WEBSOCKET_CONNECTION_TIMEOUT);
			this.client.activate();
		}
	},
	created() {
		const usesNewIb = this.$cookie.get(COOKIE_USER_USES_NEW_IB);
		const urlParams = queryString.parse(window.location.search);
		this.userUsesNewIb = usesNewIb == 'true' && urlParams?.a != RESET_NEW_IB_COOKIE;

		if (this.userUsesNewIb) {
			window.location.href = this.$t('ibLink');
		} else {
			this.$cookie.set(COOKIE_USER_USES_NEW_IB, false, { expires: '1Y', secure: true, SameSite: 'strict' });
		}

		window.addEventListener('keyup', this.keyHandler);
		this.connectToWS();
		let parsed = queryString.parse(location.search);
		this.structureId = parsed.structureId;
	},
	mounted() {
		this.gtmLoginErrorDetails = window.context && window.context.gtmLoginErrorDetails ? window.context.gtmLoginErrorDetails : null;
		let cookieVisited = this.$cookie.get(COOKIE_VISITED);
		let cookiePanelType = this.$cookie.get(COOKIE_PANEL_TYPE);
		if (cookiePanelType) {
			this.panelType = cookiePanelType;
		}
		if (this.gtmLoginErrorDetails) {
			this.$gtm.trackEvent({
				event: GTM_LOGIN_ERROR_EVENT,
				loginType: this.panelType === 'account-number' ? GTM_ACCOUNT_NUMBER_CODE : GTM_PERSONAL_ID_CODE,
				loginDetails: this.gtmLoginErrorDetails
			});
		}
		if (!cookieVisited) {
			this.$cookie.set(COOKIE_VISITED, true, { secure: true, SameSite: 'strict' });
		}
	}
};
